import React from 'react';

import {
	Field,
	Form,
	Formik,
	FormikProps,
} from 'formik';
import * as Yup from 'yup';

import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { BaseParams } from '@common/react/objects/BaseParams';

import { rest } from '@app/components/Api';
import { CustomPhoneControl } from '@app/components/Forms/CustomPhoneControl/CustomPhoneControl';
import { zipCodeValidator } from '@app/components/LocalCommon/Utils/Validators';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import CustomReCAPTCHA from '@app/components/UI/CustomReCAPTCHA/CustomReCAPTCHA';

const captchaKey = '6LezTMIcAAAAACaXvLNse5bmTJpwqs0Ezm_bhTrU';
const phoneOrEmptyRegex = /^(\(([0-9]{3})\)\s([0-9]{3})-([0-9]{4})|\+([0-9]{11})|)$/;

const links: Array<{ h4: string; p: string; btn: string; link: string }> = [
	{
		h4: 'Request Brochures',
		p: 'Order FREE Lap of Love brochures or business cards for distribution at your clinic.',
		btn: 'Order Brochures',
		link: 'https://info.lapoflove.com/clinic-resources#brochures',
	},
	{
		h4: 'Your Service Area',
		p: 'Locate areas with Lap of Love veterinarians so you can guide families to local services available to them.',
		btn: 'Lap of Love Locations',
		link: 'https://www.lapoflove.com/find-a-vet',
	},
	{
		h4: 'Frequently Asked Questions',
		p: 'Learn about the many ways Lap of Love can assist your practice.',
		btn: 'View FAQs',
		link: 'https://info.lapoflove.com/clinic-faq',
	},
];

interface ButtonSubmitProps {
	loading: boolean;
	error: string;
	formikBag: FormikProps<any>;
}

interface SectionFormsProps {
	loginRef: any;
	signupRef: any;
}

interface FormResult {
	redirectUri: string;
}

const signUpValidationSchema = Yup.object({
	firstName: Yup.string().required('First Name is a required field'),
	lastName: Yup.string().required('Last Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	zip: zipCodeValidator(),
	phoneNumber: Yup.string().matches(phoneOrEmptyRegex, 'Invalid phone number'),
	role: Yup.string().required('Please select a role'),
	captcha: Yup.string().required('Please check the box to submit this form'),
	veterinaryCollege: Yup.string().when('role', {
		is: 'VS',
		then: (schema) => schema.required('Veterinary Medicine College is a required field'),
		otherwise: (schema) => schema.notRequired(),
	}),
	graduationYear: Yup.string()
		.matches(/^[0-9]{4}$/, 'Anticipated graduation year must be 4 digits')
		.when('role', {
			is: 'VS',
			then: (schema) => schema.required('Anticipated graduation year is a required field'),
			otherwise: (schema) => schema.notRequired(),
		}),
});

const loginValidationSchema = Yup.object({
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const handleSubmit = (
	url: string,
	values: BaseParams,
	setLoading: (e: boolean) => void,
	setError: (e: string) => void,
) => {
	setLoading(true);

	rest.post<FormResult>(url, values)
		.then((response) => {
			if (response.redirectUri) {
				window.location.href = response.redirectUri;
			}
		})
		.catch(() => {
			setError('Service Unavailble. Please try again later.');
			setTimeout(() => setLoading(false), 300);
			setTimeout(() => setError(''), 10000);
		})
		.finally(() => {
			setTimeout(() => setLoading(false), 300);
		});
};

const ButtonSubmit: React.FC<ButtonSubmitProps> = ({ loading, error, formikBag }) => {
	return !loading ? (
		!error.length ? (
			<button
				type="submit"
				className={`btn btn-primary ${Object.entries(formikBag.errors).length !== 0 ? 'disabled' : ''}`}
				disabled={Object.entries(formikBag.errors).length !== 0}
			>
				Submit
			</button>
		) : (
			<div className="error-text">{error}</div>
		)
	) : (
		<BounceLoader />
	);
};

const SectionForms: React.FC<SectionFormsProps> = ({ loginRef, signupRef }) => {
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>('');

	return (
		<section className="forms">
			<div className="forms-wrap">
				<div className="forms-wrap-form forms-wrap-form__login" ref={loginRef}>
					<Formik
						onSubmit={(values) => handleSubmit('v1/form/login', values, setLoading, setError)}
						initialValues={{ email: '' }}
						validationSchema={loginValidationSchema}
					>
						{(formikBag) => {
							return (
								<>
									<Form autoComplete="off">
										<div className="h3">Veterinary Resource Center Log In</div>
										<CustomInput
											label="Email"
											name="email"
											formikBag={formikBag}
											classNameWrap="form-group"
											classNameInput="form-control"
											removeSpacesAll
										/>
										<FormikField
											containerClassName="captcha"
											fieldName="captcha"
											render={() => <CustomReCAPTCHA formikBag={formikBag} />}
										/>
										<ButtonSubmit loading={loading} error={error} formikBag={formikBag} />
									</Form>
								</>
							);
						}}
					</Formik>
				</div>
				<div className="forms-wrap-form forms-wrap-form__signup" ref={signupRef}>
					<Formik
						onSubmit={(values) => handleSubmit('v1/form/register', values, setLoading, setError)}
						initialValues={{
							firstName: '',
							lastName: '',
							email: '',
							zip: '',
							phoneNumber: '',
							role: '',
						}}
						validationSchema={signUpValidationSchema}
					>
						{(formikBag) => {
							return (
								<>
									<Form>
										<div className="h3">
											Veterinary Resource Center
											<br />
											New User Sign-Up
										</div>
										<div className="row-2">
											<CustomInput
												label="First name"
												name="firstName"
												formikBag={formikBag}
												classNameWrap="form-group"
												classNameInput="form-control"
											/>
											<CustomInput
												label="Last name"
												name="lastName"
												formikBag={formikBag}
												classNameWrap="form-group"
												classNameInput="form-control"
											/>
										</div>
										<div className="row-2">
											<CustomInput
												label="Email"
												name="email"
												formikBag={formikBag}
												classNameWrap="form-group"
												classNameInput="form-control"
												removeSpacesAll
											/>
											<CustomInput
												label="Postal code"
												name="zip"
												formikBag={formikBag}
												classNameWrap="form-group"
												classNameInput="form-control"
												removeSpacesAll
											/>
										</div>
										<div className="phone-field-wrapper">
											<FormikField
												title="Mobile phone number"
												fieldName="phoneNumber"
												showValidateAfterSubmit
												render={(fieldProps) => {
													return (
														<>
															<CustomPhoneControl
																placeholder=""
																fieldProps={fieldProps}
																onChange={(val) => val === '+1' && formikBag.setFieldValue('phoneNumber', '')}
															/>
															<div className="custom-input__error">
																{(formikBag.errors.phoneNumber && formikBag.touched.phoneNumber) ? formikBag.errors.phoneNumber : ''}
															</div>
														</>
													);
												}}
											/>
										</div>
										<FormikField
											title="Your current role"
											fieldName="role"
											render={() => (
												<div className="radio-block">
													<label>
														<Field type="radio" name="role" value="V" />
														Veterinarian
													</label>
													<label>
														<Field type="radio" name="role" value="VS" />
														Veterinary Student
													</label>
													<label>
														<Field type="radio" name="role" value="VT" />
														Veterinary Technician
													</label>
													<label>
														<Field type="radio" name="role" value="Other Veterinary Professional" />
														Other Veterinary Professional
													</label>
												</div>
											)}
										/>

										{formikBag.values.role === 'VS' && (
											<div className="fields-vet-student">
												<CustomInput
													label="Which Veterinary Medicine College are you attending?"
													name="veterinaryCollege"
													formikBag={formikBag}
													classNameWrap="form-group veterinary-college"
													classNameInput="form-control"
												/>
												<CustomInput
													label="Anticipated graduation year:"
													name="graduationYear"
													formikBag={formikBag}
													onChange={(val) => {
														formikBag.setFieldValue('graduationYear', val.replace(/[^0-9]/g, '').slice(0, 4));
													}}
													classNameWrap="form-group"
													classNameInput="form-control"
												/>
											</div>
										)}

										<div className="checkbox">
											<input type="checkbox" name="checked" id="checked" />
											<label htmlFor="checked">
												Yes I'd like to subscribe to The Compassion Movement, Lap of Love's quarterly e-newsletter
											</label>
										</div>
										<FormikField
											fieldName="captcha"
											containerClassName="captcha"
											render={() => <CustomReCAPTCHA formikBag={formikBag} />}
										/>
										<ButtonSubmit loading={loading} error={error} formikBag={formikBag} />
									</Form>
								</>
							);
						}}
					</Formik>
				</div>
				<div className="forms-wrap-links">
					{links.map((link, i) => (
						<div key={i} className="forms-wrap-links__link">
							<h4>{link.h4}</h4>
							<p>{link.p}</p>
							<a href={link.link} target="_blank" rel="nofollow noreferrer">
								<button className="btn btn-primary">{link.btn}</button>
							</a>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default SectionForms;
